/**
 * Roraima's setting sun, in the shared 120×120 LINE asset box.
 *
 * The owner's pick for the passcode dots (2026-08-13), replacing the tumbleweed:
 * "batan güneş daha iyi olur". It is the theme's own subject — the whole palette
 * is named after a sunset over the tepui — and it is already the thing the menu
 * strip puts behind the walls, so the dots now echo the strip instead of
 * introducing a third object.
 *
 * ONE SUN, FOUR COLOURS (owner 2026-08-13: "hepsi 3.sü olsun sadece renkleri
 * farklı olsun"). An earlier pass sank the sun a step on each digit and shortened
 * its rays with it, so the four played a sunset in order. The owner picked the
 * THIRD of those — the sun already low, a broad cap over the horizon — and asked
 * for all four to be it, varying only in colour.
 *
 * This is the rule Sakura's blossoms settled and it holds here for the same
 * reason: the four dots are one design, and what varies inside it stays small.
 * The colours still run gold to low coral, so the sunset is in the palette
 * rather than in the geometry.
 *
 * KAWAII in the family's specific sense — flat shapes, thick rounded strokes, a
 * clean silhouette (the recipe Sakura, Tokyo and Everest set). The one gradient
 * is top-to-bottom inside the disc, which reads as a lit sphere rather than as
 * atmosphere.
 */

/** Where the horizon sits. Everything above it is sky, everything below is cut. */
export const SUN_HORIZON = 92;
/** The disc's radius. */
export const SUN_R = 27;

/**
 * The sun's centre, the SAME for all four dots. 80 is the owner's pick out of
 * the sinking sequence that used to run 64/72/80/88: low enough that the disc
 * reads as going down rather than as a plain sun, high enough that the cap is
 * still most of a circle. It also leaves the whole composition — rays, disc,
 * horizon — centred in the 120 box.
 */
export const SUN_CY = 80;

/** Ray length, likewise one value for all four. */
export const SUN_RAY_LEN = 7;

/**
 * Light and deep tones per digit — the ONLY thing that changes between the four.
 * From the theme's own palette: Dry Grass, then Amber Sand, Coral Canyon and
 * Sunset Mesa. The pair is the disc's gradient, top to bottom, and the deep one
 * also paints the horizon, the halo and the clouds, so each dot is one family.
 */
export const SUN_COLORS: { light: string; deep: string }[] = [
  { light: '#F0D48A', deep: '#DDBE6D' },
  { light: '#E9BE7E', deep: '#D69568' },
  { light: '#E0906F', deep: '#D17458' },
  { light: '#D4735A', deep: '#CC654E' },
];

/** The lit stop on the Home tab's sun, one step off the ON colour so the disc
 *  reads as lit rather than flat. Lives here rather than in the tepui module,
 *  which is about a motif this theme no longer draws. */
export const SUN_LIGHT = '#F8C4C4';

/** ONE outline tone for every unentered dot — the frame never changes per digit
 *  (the rule Aurora's orbs settled). Warm grey against the skin's dark brown. */
export const SUN_OFF = '#8A7268';

/**
 * The rays for one digit: short strokes over the disc's UPPER half only, since
 * the lower ones would be under the horizon anyway. `n` odd keeps one ray
 * straight up, which is what stops the fan from looking lopsided.
 */
export function sunRays( cy: number, len: number, n = 7 ): string {
  const f = ( v: number ) => +v.toFixed( 1 );
  const out: string[] = [];
  for ( let i = 0; i < n; i++ ) {
    // 1.06π..1.94π — the upper arc, stopping short of the horizon on both sides.
    const a = Math.PI * ( 1.06 + ( i / ( n - 1 ) ) * 0.88 );
    const r0 = SUN_R + 7;
    const r1 = r0 + len;
    out.push( `M${ f( 60 + Math.cos( a ) * r0 ) },${ f( cy + Math.sin( a ) * r0 ) }`
      + ` L${ f( 60 + Math.cos( a ) * r1 ) },${ f( cy + Math.sin( a ) * r1 ) }` );
  }
  return out.join( ' ' );
}

/** The horizon the sun goes down behind, `half` units either side of centre. */
export function sunHorizonD( half: number ): string {
  return `M${ 60 - half },${ SUN_HORIZON } L${ 60 + half },${ SUN_HORIZON }`;
}

/** The passcode's horizon: wide, running most of the tile. */
export const SUN_HORIZON_D = sunHorizonD( 48 );

/**
 * The TAB's horizon, cut back to the rays' own span.
 *
 * On the tab this line is the widest thing in the glyph — 96 units against the
 * disc's 54 — so it both dominates the eye and dominates an ink-bbox
 * measurement, which is what made a "matched" Home button still read small
 * (owner 2026-08-13: "alttaki çizgiden dolayı hissiyat yanıltıyor"). Cut to the
 * rays, the glyph measures as the SUN rather than as the line under it. The
 * passcode keeps the wide one, where the extra width reads as horizon and there
 * is nothing beside it to compare against.
 */
export const SUN_TAB_HORIZON_D = sunHorizonD( SUN_R + 14 );

/**
 * Vertical nudge for the TAB glyph, so it sits centred beside the Phosphor icons
 * instead of hanging low (owner 2026-08-13: "altta hizalı olmuş ortalı olmalı").
 *
 * The composition's ink runs from the ray tips at SUN_CY − (R + 7 + rays) = 39
 * down to the horizon plus half its stroke = 95, so its centre is 67 — seven
 * units below the box's own 60. The Phosphor masks ARE centred in their boxes,
 * so side by side the sun reads as bottom-aligned. Applied on the tab only: the
 * passcode dot is drawn alone with nothing to line up against, and its
 * composition is already signed off.
 */
export const SUN_TAB_DY = -7;

/**
 * How much the TAB glyph's artwork is scaled up INSIDE its 120 box.
 *
 * Why the art and not the element: `.glyph-svg` is a flex item, so a width over
 * 100% of the tile is silently shrunk back by flexbox. The classic tile is
 * 128×150 and the sun hit that ceiling, which is why raising its size factor
 * from 1.45 to 1.62 to 1.9 changed nothing at all and the owner kept reading the
 * button as small while the shorter 80×56 _g tile — which never reached the
 * ceiling — looked right. Growing the drawing inside the viewBox has no such
 * limit, and it leaves every other theme's glyph untouched (Sequoia's redwood
 * also sits just over 100% and is clamped today; unclamping it would move a
 * theme that is already live).
 *
 * At 1.36 the classic Home glyph measures 1.79× a Phosphor neighbour's ink,
 * which is what the _g slot the owner signed off already measures (1.80×).
 */
export const SUN_TAB_ART_SCALE = 1.36;

/** Centre-anchored: scales the art about the box centre and applies the nudge. */
export const SUN_TAB_TRANSFORM =
  `translate(60,${ 60 + SUN_TAB_DY }) scale(${ SUN_TAB_ART_SCALE }) translate(-60,-60)`;
